// AgentsTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Bot, Plus, List, Play, Pause, Settings } from 'lucide-react';
import { STATUS_COLORS } from '../constants';

/**
 * Agents tab component for managing AI workforce agents
 * 
 * @param {Object} props
 * @param {Array} props.agents - List of AI agents
 * @param {function} props.onShowTemplateDialog - Callback to open template dialog
 * @param {function} props.onShowCreateAgentDialog - Callback to open create agent dialog
 * @param {function} props.onActivateAgent - Callback when activating an agent (receives agent id)
 * @param {function} props.onSuspendAgent - Callback when suspending an agent (receives agent id)
 * @param {function} props.onSelectAgent - Callback when selecting an agent for settings (receives agent)
 */
export const AgentsTab = ({
  agents = [],
  onShowTemplateDialog,
  onShowCreateAgentDialog,
  onActivateAgent,
  onSuspendAgent,
  onSelectAgent
}) => {
  return (
    <TabsContent value="agents" className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">AI Agents</h3>
          <p className="text-sm text-muted-foreground">Manage your AI workforce</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onShowTemplateDialog} variant="outline">
            <List className="h-4 w-4 mr-2" />
            From Template
          </Button>
          <Button onClick={onShowCreateAgentDialog} data-testid="create-agent-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Custom Agent
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map(agent => (
          <Card key={agent.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-14 w-14 bg-gradient-to-br from-violet-100 to-purple-100">
                    <AvatarFallback className="bg-transparent text-violet-700 font-bold text-xl">
                      {agent.name?.[0] || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-bold text-lg">{agent.name || 'Unnamed Agent'}</h3>
                    <p className="text-sm text-muted-foreground">{agent.role_title || 'AI Agent'}</p>
                  </div>
                </div>
                <Badge className={STATUS_COLORS[agent.status] || STATUS_COLORS.draft}>{agent.status || 'draft'}</Badge>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{agent.mission || 'No mission defined'}</p>
              
              <div className="flex flex-wrap gap-1 mb-4">
                {agent.automates_well?.slice(0, 3).map((skill, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{skill}</Badge>
                ))}
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center mb-4">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-lg font-bold text-emerald-600">{agent.tasks_completed || 0}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-lg font-bold text-red-600">{agent.tasks_failed || 0}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-lg font-bold text-blue-600">{agent.active_tasks || 0}</p>
                  <p className="text-xs text-muted-foreground">Active</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                {agent.status === 'draft' && (
                  <Button className="flex-1" onClick={() => onActivateAgent(agent.id)}>
                    <Play className="h-4 w-4 mr-1" />
                    Activate
                  </Button>
                )}
                {agent.status === 'active' && (
                  <Button variant="outline" className="flex-1" onClick={() => onSuspendAgent(agent.id)}>
                    <Pause className="h-4 w-4 mr-1" />
                    Suspend
                  </Button>
                )}
                {agent.status === 'suspended' && (
                  <Button className="flex-1" onClick={() => onActivateAgent(agent.id)}>
                    <Play className="h-4 w-4 mr-1" />
                    Reactivate
                  </Button>
                )}
                <Button variant="outline" onClick={() => onSelectAgent(agent)}>
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {agents.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Bot className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No AI Agents Yet</h3>
              <p className="text-muted-foreground mb-4">Onboard your first virtual employee to get started</p>
              <Button onClick={onShowTemplateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Onboard First Agent
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </TabsContent>
  );
};

export default AgentsTab;
